<template>
    <section class="person-section">
        <div class="person-section__container">
            <CardTitle v-if="!hideTitle" :module="item" />
            <ScrollRow>
                <div v-for="(cell, idx) in props.item.fieldData"
                    :key="String((cell as Record<string, unknown>)?.id ?? idx)" class="person-card"
                    @click="handleCellClick(cell)">
                    <!-- 视频/图片缩略图 -->
                    <MediaCard class="person-card__media" :url="getCellVideo(cell) || getCellCover(cell)"
                        :name="getName(cell)" video-popup />

                    <!-- 头像+姓名+职位 -->
                    <div class="person-card__info">
                        <div v-if="getAvatar(cell)" class="person-card__avatar">
                            <img :src="getAvatar(cell)" :alt="getName(cell)" loading="lazy" />
                        </div>
                        <div class="person-card__meta">
                            <h4 v-if="getName(cell)" class="person-card__name">{{ getName(cell) }}</h4>
                            <span v-if="getRole(cell)" class="person-card__role">{{ getRole(cell) }}</span>
                        </div>
                    </div>
                    <!-- 引用文字 -->
                    <p v-if="getQuote(cell)" class="person-card__quote">{{ getQuote(cell) }}</p>
                </div>
            </ScrollRow>
        </div>
    </section>
</template>

<script setup lang="ts">
import type { Module } from '@/api/page_config'
import { getStrWithRecord, jumpToUrl } from '.';

const props = withDefaults(defineProps<{
    item: Module
    hideTitle?: boolean
}>(), { hideTitle: false })

const emit = defineEmits<{
    (e: 'click', item: Module, cell?: unknown): void
}>()

function getCellCover(cell: unknown): string {
    return getStrWithRecord(cell, 'image')
}

function getCellVideo(cell: unknown): string {
    return getStrWithRecord(cell, 'video')
}

function getAvatar(cell: unknown): string {
    return getStrWithRecord(cell, 'avatar')
}
function getName(cell: unknown): string {
    return getStrWithRecord(cell, 'name')
}
function getRole(cell: unknown): string {
    return getStrWithRecord(cell, 'userTitle')
}
function getQuote(cell: unknown): string {
    return getStrWithRecord(cell, 'des')
}

function handleCellClick(cell: unknown): void {
    emit('click', props.item, cell)
    const url = getStrWithRecord(cell, 'url')
    jumpToUrl(url)
}

</script>

<style scoped lang="scss">
@use '@/assets/styles/variables' as *;

$card-radius: 12px;

.person-section {
    width: 100%;
}

.person-section__container {
    max-width: $container-max-width;
    margin: 0 auto;
    padding: 0 1rem;

    @media (min-width: $breakpoint-md) {
        padding: 0 2rem;
    }
}

.person-card {
    flex-shrink: 0;
    width: 100%;
    scroll-snap-align: start;
    cursor: pointer;
    display: flex;
    flex-direction: column;

    @media (min-width: $breakpoint-sm) {
        width: 260px;
    }

    @media (min-width: $breakpoint-md) {
        width: 260px;
        max-width: none;
    }

    &__media {
        position: relative;
        width: 100%;
        aspect-ratio: 16 / 10;
        border-radius: $card-radius;
        overflow: hidden;
        background: $color-bg-tertiary;

        video,
        img {
            width: 100%;
            height: 100%;
            object-fit: cover;
            transition: transform 0.5s ease;
        }
    }

    &:hover .person-card__media img,
    &:hover .person-card__media video {
        transform: scale(1.04);
    }

    &__play-btn {
        position: absolute;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
        width: 48px;
        height: 48px;
        border-radius: 50%;
        background: #fff;
        display: flex;
        align-items: center;
        justify-content: center;
        color: #ff6d00;
        box-shadow: 0 4px 16px rgba(0, 0, 0, 0.15);

        svg {
            width: 20px;
            height: 20px;
            margin-left: 2px;
        }

        @media (min-width: $breakpoint-md) {
            width: 56px;
            height: 56px;

            svg {
                width: 24px;
                height: 24px;
            }
        }
    }

    &__info {
        display: flex;
        align-items: center;
        gap: 0.75rem;
        margin-top: 1rem;
    }

    &__avatar {
        width: 44px;
        height: 44px;
        border-radius: 50%;
        overflow: hidden;
        flex-shrink: 0;
        background: $color-bg-tertiary;

        img {
            width: 100%;
            height: 100%;
            object-fit: cover;
        }
    }

    &__meta {
        display: flex;
        flex-direction: column;
        gap: 0.125rem;
        min-width: 0;
    }

    &__name {
        font-size: 1.125rem;
        font-weight: 800;
        color: $color-text;
        margin: 0;
        line-height: 1.3;

        @media (min-width: $breakpoint-md) {
            font-size: 1.25rem;
        }
    }

    &__role {
        font-size: 0.75rem;
        color: $color-text-secondary;
        line-height: 1.4;
    }

    &__quote {
        font-size: 0.75rem;
        color: $color-text-secondary;
        line-height: 1.7;
        margin: 0.75rem 0 0;

        @media (min-width: $breakpoint-md) {
            font-size: $font-size-sm;
        }
    }
}
</style>
